import { Head } from '@components/head';
import { CopyLinkButton } from '@components/incentives/copy-link-button';
import { DonationExperience } from '@components/incentives/donation-experience';
import { DuplicateIncentiveButton } from '@components/incentives/duplicate-incentive/duplicate-incentive-button';
import { EditFeaturedCharities } from '@components/incentives/featured-charities';
import { IncentiveSettings } from '@components/incentives/incentive-settings';
import { Overview } from '@components/incentives/overview';
import { ToggleIncentive } from '@components/incentives/toggle-incentive';
import { ActionBar } from '@components/ui/action-bar';
import { Tabs } from '@components/ui/tabs';
import NavigateNextSharpIcon from '@mui/icons-material/NavigateNextSharp';
import {
  Box,
  Breadcrumbs,
  Container,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useGetCollection } from '@queries/collections';
import { useGetIncentive } from '@queries/incentives';
import { donorAppRoutes, routes } from '@routes';
import { FunctionComponent, PropsWithChildren } from 'react';
import { Link as LinkReact, useParams } from 'react-router-dom';

const Title: FunctionComponent<{
  gid: string;
  isLoading: boolean;
  name?: string;
  slug?: string;
}> = (props) => {
  const { gid, isLoading, name, slug } = props;
  return (
    <Stack direction="column">
      <Breadcrumbs separator={<NavigateNextSharpIcon fontSize="small" />}>
        <Link
          component={LinkReact}
          to={routes.incentives}
          underline="hover"
          color="text.primary"
        >
          Incentives
        </Link>
        <Typography color="text.primary">{gid}</Typography>
      </Breadcrumbs>
      {isLoading ? (
        <Skeleton animation="wave" variant="rounded" height={35} width={100} />
      ) : (
        <Stack direction="row">
          <Typography variant="h4" color="text.primary" pr={2}>
            {name}
          </Typography>
          <DuplicateIncentiveButton gid={gid} name={name} />
          {slug && (
            <CopyLinkButton
              text={donorAppRoutes
                .generator({
                  queryParams: {},
                  slug,
                })
                .toString()}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};

export const EditLayout: FunctionComponent<
  PropsWithChildren<{ gid: string }>
> = (props) => {
  const { children, gid } = props;
  const { data, isLoading } = useGetIncentive({
    body: {},
    gid: gid!,
  });
  const activeIncentive = data?.data;
  useGetCollection(
    {
      gid: activeIncentive?.collection.gid!,
    },
    { enabled: Boolean(activeIncentive?.collection.gid) },
  );
  const incentiveGid = gid!;
  return (
    <>
      <Head title="Incentives" />
      <Container maxWidth="xl">
        <ActionBar
          title={
            <Title
              gid={incentiveGid}
              isLoading={isLoading}
              name={activeIncentive?.name}
              slug={activeIncentive?.slug}
            />
          }
          primaryAction={
            isLoading ? (
              <Skeleton
                animation="wave"
                variant="rounded"
                height={35}
                width={100}
              />
            ) : (
              <ToggleIncentive
                gid={gid!}
                initialStatus={activeIncentive?.status}
              />
            )
          }
        />
        {children}
      </Container>
    </>
  );
};

export const EditIncentivePage: FunctionComponent = () => {
  const { gid } = useParams<{ gid: string }>();
  const incentiveGid = gid!;
  return (
    <EditLayout gid={incentiveGid}>
      <Tabs
        Container={({ children }) => <Box pt={3}>{children}</Box>}
        tabs={[
          {
            element: <Overview gid={incentiveGid} />,
            props: { label: 'Overview' },
          },
          {
            element: <IncentiveSettings gid={incentiveGid} />,
            props: { label: 'Settings' },
          },
          {
            element: <EditFeaturedCharities gid={incentiveGid} />,
            props: { label: 'Featured Charities' },
          },
          {
            element: <DonationExperience gid={incentiveGid} />,
            props: { label: 'Donation Experience' },
          },
        ]}
        variant="standard"
        orientation="horizontal"
      />
    </EditLayout>
  );
};
